import React from "react";
import { OktaAuth } from "@okta/okta-auth-js";
import jwt_decode from "jwt-decode";
import { oktaAuthConfig } from "modules/okta/oktaConfig";
import { CONSTANTS } from "./constants";
import { CONFIGURATION } from "utilities/configuration";
import sanitizeHtml from "sanitize-html";
import Moment from "moment";

async function getOktaToken() {
    const authClient = new OktaAuth(oktaAuthConfig);
    const accessToken = await authClient.tokenManager.get("accessToken");

    return accessToken;
}

async function validateOkta() {
    const authClient = new OktaAuth(oktaAuthConfig);

    if (await authClient.session.exists()) {
        let response = await authClient.token.getWithoutPrompt();

        authClient.tokenManager.setTokens(response.tokens);
        return true;
    }

    return false;
}

function getUserType(token) {
    if(!token && typeof window === undefined) return;

    let accessToken = token;
    if(!accessToken) {
        let authentication = localStorage.getItem("okta-token-storage");    
        accessToken = JSON.parse(authentication)?.accessToken?.accessToken;
    }

    if (accessToken) {
        let decodedToken = jwt_decode(accessToken);
        let qmaGroups = decodedToken["qma.groups"];
        let agrp = decodedToken["agrp"];
        let user = "CUSTOMER";

        if (agrp) {
            user = agrp?.length > 0 ? "AGENT" : "";
        }
        if (qmaGroups) {
            user = qmaGroups?.length > 0 && qmaGroups[0].toUpperCase();
        }

        return user;
    }
}

function isIQMUserRole(user) {
    return user === CONSTANTS.IQM;
}

function isCustomerUser(){
    let userData = getUserType();
    return userData === "CUSTOMER";
}

function isIQMUser() {
    let userData = getUserType();
    return userData === CONSTANTS.IQM;
}

/**
 * To detect if the current user is agent
 * @returns {Boolean} If current user is agent, it will return true, otherwise, it returns false.
 */
function isAgent(userType) {
    if(userType) {
        return userType === CONSTANTS.AGENT;
    }
    else {
        let userData = getUserType();
        return userData === CONSTANTS.AGENT;
    }
}

function getNumberPostFixText(index) {
    return index === 0 ? "st" : index === 1 ? "nd" : index === 2 ? "rd" : "th";
}

function getNumberByLanguage(index) {
    const localize = getLanguage();
    if (localize === "en") {
        return index === 0 ? "st" : index === 1 ? "nd" : index === 2 ? "rd" : "th";
    } else {
        return index === 0 ? "er" : index === 1 ? "e" : index === 2 ? "e" : "e";
    }
}

function translateCurrencyFormat(amount, lang) {
    const localize = lang ?? getLanguage();
    const formatted = new Intl.NumberFormat((localize === "fr" ? "fr-CA" : "en-CA"), {
        style: "currency",
        currency: "CAD"
    }).format(amount);
    return formatted;

}

function calculateDuration(start, end) {
    if (!start || !end) return 0;

    const s = new Date(start);
    const e = new Date(end);

    // Normalize to local midnight
    const startDate = new Date(s.getFullYear(), s.getMonth(), s.getDate());
    const endDate   = new Date(e.getFullYear(), e.getMonth(), e.getDate());

    const MS_PER_DAY = 24 * 60 * 60 * 1000;

    return Math.round((endDate - startDate) / MS_PER_DAY);
}

function IsEqualDate(dt1, dt2) {

    if(dt1 && dt2) {
        const d1 = new Date(dt1);
        const d2 = new Date(dt2);

        // Normalize to local midnight
        const date1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
        const date2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());

        return date1.getTime() === date2.getTime();
    }
    return false;
}

function scrollToSection(scrollElementId, offset = 0) {
    const element = document.getElementById(scrollElementId);
    const top = (element?.offsetTop ?? offset) - offset;

    window?.scrollTo(0, top);
}

function getProductType(props) {
    let { data } = props;
    let { quote, optionIndex, deviationOptionIndex } = data;

    let { productTypes, deviationTypes } = quote;
    if (productTypes && productTypes?.length > 0 && productTypes[optionIndex]?.productType?.code !== "" && productTypes[optionIndex]?.productType?.code !== "Deviation") {
        return productTypes[optionIndex];
    }
    else if (deviationTypes && deviationTypes.length > 0 && deviationTypes[deviationOptionIndex]?.productType?.code === "Deviation") {
        return deviationTypes[deviationOptionIndex];
    }
    else {
        return null;
    }
}

function getProductTypeFromState(state) {
    let { quote, optionIndex } = state;
    let { productTypes } = quote;

    if (productTypes && productTypes?.length > 0) {
        return productTypes[optionIndex];
    }
    return null;
}

function getDeviationProductTypeFromState(state) {
    let { quote, deviationOptionIndex } = state;
    let { deviationTypes } = quote;
    if (deviationTypes && deviationTypes?.length > 0) {
        return deviationTypes[deviationOptionIndex];
    }
    return null;
}
function getDeviationProducts(quoteOption) {
    let products = [];

    quoteOption?.deviationItinerary?.deviationRequest?.forEach((option) => {
        let destination = option.destination;
        option?.products?.forEach(product => {
            let p = { ...product, destination };
            if(products.length != 2) products.push(p);
        });
    });
    return products;
}

function getLanguage() {
    if(typeof window === undefined) return "en";
    return    localStorage.getItem("userLanguage");
}

function setLanguage(language) {
    if(typeof window !== undefined) {
        localStorage.setItem("userLanguage", language);
    }
}

function clearStorage() {
    if(typeof window !== undefined) {
        localStorage.removeItem("userLanguage");
        localStorage.removeItem("okta-original-uri-storage");
        localStorage.removeItem("okta-cache-storage");
        localStorage.removeItem("okta-shared-transaction-storage");
    }
}

function hasActiveItineraries(quoteOption) {
    if(quoteOption?.productType?.code?.toLowerCase() === "deviation"){
        return quoteOption?.deviationItinerary?.status !== "Inactive";
    }
    else {
        return quoteOption?.products?.filter(product => product?.itineraries?.filter(itinerary => itinerary.status !== "Inactive")?.length > 0 )?.length > 0;
    }
}


/**
 * Append a zero "0" at the end of string if the string ends with "."
 *
 * @param {String} value - a string.
 * @returns {String} original string or new string with a zero appended at the end.
 */
function appendZero(value) {
    const pattern = /\.$/;
    if (pattern.test(value)) {
        return value + "0";
    }
    else {
        return value;
    }
}

/**
 * Convert a string to float type
 *
 * @param {String} value - a string.
 * @returns {Float} a float number or null.
 */
function toFloat(value) {
    const num = parseFloat(value.replace(",", ""));
    return isNaN(num) ? null : num;
};

/**
 * Convert a string to int type
 *
 * @param {String} value - a string.
 * @returns {Int} a int number or null.
 */
function toInt(value) {
    const num =  parseInt(value.replace(",", "")); 
    return isNaN(num) ? null : num;
};

/**
 * To standardize the language
 *
 * @param {String} value - a string.
 * @returns {String} en vs fr.
 */
function convertLanguage(language) {
    if(language) {
        if(language.toLowerCase().startsWith("en")) return "en";
        if(language.toLowerCase().startsWith("fr")) return "fr";
    }
    return "en";
}

/**
 * To get the date of today
 * @returns {String} a string presenting today.
 */
function getTodayDate() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.toISOString(); // YYYY-MM-DD, it will be safe
}

/**
 * To round the number with 2 decimal places
 *
 * @param {Number} num - a number.
 * @returns {Number} a number with 2 decimal places.
 */
function roundNumber(num) {
    return Number(num.toFixed(2));
}

/**
 * To remove the dot at the end if present
 *
 * @param {String} value - a string.
 * @returns {String} a string.
 */
function removeEndDot(value) {
    if(value) {
        return  value.replace(/\.$/, "");
    }
    return value;
}

/**
 * To detect if the leading zero in the number is correct.
 *
 * @param {String} value - a string.
 * @returns {Boolean} true vs false.
 */
function isLeadingCorrectZero(value) {
    // Allow empty string if user is typing
    if (value === "") return true;
  
    // Valid if:
    // - starts with non-zero digit, possibly followed by digits and optional decimal
    // - OR is "0", "0." or "0.xxx"
    //const regex = /^(0|[1-9]\d*)(\.\d*)?$/;
    const regex = /^(0|0\.[0-9]+|[1-9][0-9]*(\.[0-9]+)?)$/; // "0." is not allowed
    return regex.test(value);
}


/**
 * Safely sanitize HTML content using sanitizeHtml.
 * Allows only a minimal, safe subset of tags/attributes.
 */
function cleanContent(content) {
    if (!content || typeof content !== "string") return "";

    return sanitizeHtml(content, {
        allowedTags: ["p", "div", "b", "i", "em", "strong", "a", "p", "ul", "li", "br", "span", "table", "td", "tr", "th", "u", "tbody"],
        allowedAttributes: false,
        disallowedTagsMode: "discard" // strips bad tags entirely
    });
}

/**
 * Converts sanitized HTML to React elements safely.
 */
function parseHtml(html, tag = "div", extraProps = {}) {

    const clean = cleanContent(html);

    // React best practice: never return raw strings here, always a valid element.
    return React.createElement(tag || "div", {
        dangerouslySetInnerHTML: { __html: clean },
        style: { wordWrap: "break-word" },
        ...extraProps // merge any additional props
    });
}

function isValidValue(value) {
    return value !== null && value !== "";
}

function isFilledValue(value) {
    return value !== undefined && value !== null && value !== "";
}

function quoteCreationWizardTotalSteps(productTypeCode) {
    return CONFIGURATION.QUOTE_WIZARD_TOTAL_STEPS - (flightSearchAvailable(productTypeCode) ? 0 : 1);
}

function flightSearchAvailable(productTypeCode) {
    return (CONFIGURATION.GSO_FLIGHT_SEARCH_ENABLE && productTypeCode?.toLowerCase() === CONSTANTS.PACKAGE);
}

function getQuoteCreationWizardNextStep(currentStep, productTypeCode, isManualQuote) {
    const lastStep = quoteCreationWizardTotalSteps(productTypeCode);
    
    if(currentStep > (lastStep - 1)) {
        return CONFIGURATION.QUOTE_WIZARD_STEP_SUMMARY;
    }
    else {
        const _flightSearchAvailable = flightSearchAvailable(productTypeCode);
        let _currentStep = currentStep;

        if((isManualQuote || !_flightSearchAvailable) && currentStep === CONFIGURATION.QUOTE_WIZARD_STEP_OPTION_INFO_COLLECTION) {
            _currentStep = _currentStep + 1;
        }
        return _currentStep + 1;
    }
}

function getGroupEventAvailable(sfdc) {

    if(process.env.REACT_APP_BRAND === CONSTANTS.BRAND_DEFAULT) {
        if(isIQMUser() && sfdc?.data?.data?.customerType?.toLowerCase() === "public") {
            return true;
        }
        return isCustomerUser();
    }
    return false;
    
}

function getEventsByLanguage(events, language) {
    const lang = convertLanguage(language);
    if(events && events.length > 0) {
        return events.map(event => {
            return { ...event, name: event.name[lang] || event.name.en };
        });
    }
    return [];
}

function getGroupTypesByLanguage(groupTypes, language) {
    const lang = convertLanguage(language);
    if(groupTypes && groupTypes.length > 0) {
        return groupTypes.map(groupType => {
            return { ...groupType, name: groupType.name[lang] || groupType.name.en };
        });
    }
    return [];
}

function flightIncluded (filter, flightTime) {
    if(filter && flightTime) {
        let result = filter.split("-");
        if(result && result.length === 2) {

            const date = new Date();
            date.setHours(0, 0, 0, 0);
            const dateOnly = Moment(date).format("YYYY-MM-DD");

            const flightDateTime = new Date(dateOnly + " " + flightTime);

            const startDateTime = new Date(dateOnly + " " + result[0]);
            const endDateTime = new Date(dateOnly + " " + result[1]);

            if(flightDateTime >= startDateTime && flightDateTime <= endDateTime) {
                return true;
            }
        }
        return false;
    }
    return true;
}

function formatDuration(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;

    if (h === 0) return `${m}m`;
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

function getGroupType(name, groupTypes) {
    if(name) {
        const _groupType = groupTypes?.data?.find(s => s?.name?.en?.toLowerCase() === name.toLowerCase());
        return _groupType;
    }
    return null;
};

function getEventType(language, name, groupType) {
    if(name) {
        const lang = convertLanguage(language ?? "en");
        const _eventType = groupType?.events?.find(s => s?.name?.[lang]?.toLowerCase() === name.toLowerCase());
        return _eventType;
    }
    return null;
};

function buildGroupTypes(data) {
    let groupTypes = data?.groupTypes?.items.map(groupType => {
        const events = groupType?.events?.items.map(event => {
            return { name: { en: event.eventNameEn, fr: event.eventNameFr }, code: event.key };
        });
        return {
            code: groupType.key,
            name: { en: groupType.groupTypeNameEn, fr: groupType.groupTypeNameFr },
            events: events
        };
    });
    return groupTypes;
}

function getGroupTypeName(language, name, groupTypes) {
    const lang = convertLanguage(language ?? "en");
    if(lang === "en") return name;
    const _groupType = groupTypes?.data?.find(s => s?.name?.en === name);
    return _groupType?.name?.fr;
};

function isCalculatedQuote(brand) {
    let _brand = brand ?? process.env.REACT_APP_BRAND;
    return _brand?.toLowerCase() === CONSTANTS.BRAND_WVI;
}

const commonUtilities = {
    isCustomerUser,
    isIQMUser,
    isAgent,
    isIQMUserRole,
    getUserType,
    getOktaToken,
    validateOkta,
    getProductType,
    scrollToSection,
    calculateDuration,
    getNumberPostFixText,
    getProductTypeFromState,
    getDeviationProductTypeFromState,
    getNumberByLanguage,
    translateCurrencyFormat,
    getDeviationProducts,
    getLanguage,
    setLanguage,
    clearStorage,
    hasActiveItineraries,
    toFloat,
    convertLanguage,
    toInt,
    getTodayDate,
    roundNumber,
    appendZero,
    removeEndDot,
    isLeadingCorrectZero,
    parseHtml,
    cleanContent,
    isValidValue,
    isFilledValue,
    IsEqualDate,
    quoteCreationWizardTotalSteps,
    getQuoteCreationWizardNextStep,
    flightIncluded,
    formatDuration,
    flightSearchAvailable,
    getGroupEventAvailable,
    getEventsByLanguage,
    getGroupTypesByLanguage,
    getGroupType,
    getEventType,
    buildGroupTypes,
    getGroupTypeName,
    isCalculatedQuote
};

export default commonUtilities;
